import { VaultStatProps, VaultStatsSkeleton } from '../VaultStatsSkeleton';
import {
	getTimeWeightedAPY,
	getTimeWeightedRoi,
	getVaultDepositorBalance,
	getVaultDepositorNotionalNetDeposits,
} from 'src/utils/vault/math';
import { BigNum, QUOTE_PRECISION_EXP, SpotMarketConfig } from '@drift-labs/sdk';
import { useVaultOnChainData } from 'src/hooks/query/vaults/base/useVaultOnChainData';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { MarketId } from '@drift/common';
import { UiVaults } from 'src/constants/vaults';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { useVaultDepositorHistories } from 'src/hooks/query/vaults/base/useVaultDepositorHistories';
import { cj } from 'src/utils/classNames';

export const VaultDepositorStats = (props: {
	depositAssetConfig: SpotMarketConfig;
	vaultPubkey: string;
}) => {
	const getOraclePrice = useGetOraclePriceForMarket();
	const { data: vaultStats } = useVaultStats(props.vaultPubkey);
	const vaultTvlBase = vaultStats?.tvlBase;
	const marketId = MarketId.createSpotMarket(
		props.depositAssetConfig.marketIndex
	);
	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const { data: historiesLookup, isLoading: isVaultDepositorHistoryLoading } =
		useVaultDepositorHistories();
	const vaultDepositorHistory = historiesLookup?.[props.vaultPubkey] ?? [];
	const { data: onChainData } = useVaultOnChainData();
	const { data: depositorAccounts, isLoading: isDepositorLoading } =
		useVaultDepositorAccounts();
	const vaultAccountData = onChainData?.[props.vaultPubkey]?.vaultAccountData;
	const vaultDepositorAccountData =
		depositorAccounts?.[props.vaultPubkey]?.vaultDepositor;
	const isVaultDepositorLoaded = !isDepositorLoading;

	const currentUserBaseBalance = getVaultDepositorBalance(
		vaultDepositorAccountData,
		vaultAccountData,
		vaultTvlBase,
		props.depositAssetConfig.precisionExp,
		true
	);
	const currentUserNotionalBalance = getCurrentUserNotionalBalance();

	const totalBaseEarnings = getTotalBaseEarnings();
	const totalNotionalEarnings = getTotalNotionalEarnings();
	if (
		!uiVaultConfig ||
		!vaultDepositorAccountData ||
		!vaultAccountData ||
		!vaultDepositorHistory
	)
		return null;
	const isNotionalGrowthStrategy = uiVaultConfig.isNotionalGrowthStrategy;

	const timeWeightedRoi = getTimeWeightedRoi(
		vaultDepositorAccountData,
		vaultAccountData,
		uiVaultConfig,
		vaultDepositorHistory,
		isNotionalGrowthStrategy
			? QUOTE_PRECISION_EXP
			: props.depositAssetConfig.precisionExp,
		isNotionalGrowthStrategy
			? currentUserNotionalBalance
			: currentUserBaseBalance
	);
	const timeWeightedApy = getTimeWeightedAPY(
		vaultDepositorAccountData,
		vaultAccountData,
		uiVaultConfig,
		vaultDepositorHistory,
		isNotionalGrowthStrategy
			? QUOTE_PRECISION_EXP
			: props.depositAssetConfig.precisionExp,
		isNotionalGrowthStrategy
			? currentUserNotionalBalance
			: currentUserBaseBalance
	);

	function getCurrentUserNotionalBalance() {
		const oraclePrice = getOraclePrice(marketId);
		const currentUserNotionalBalance = currentUserBaseBalance
			.mul(oraclePrice ?? BigNum.from(0, QUOTE_PRECISION_EXP))
			.shiftTo(QUOTE_PRECISION_EXP);

		return currentUserNotionalBalance;
	}

	function getTotalBaseEarnings() {
		if (!vaultDepositorAccountData || !vaultAccountData)
			return BigNum.from(0, props.depositAssetConfig.precisionExp);

		const totalEarnings = currentUserBaseBalance.sub(
			BigNum.from(
				vaultDepositorAccountData.netDeposits,
				props.depositAssetConfig.precisionExp
			)
		);

		return totalEarnings;
	}

	function getTotalNotionalEarnings() {
		// we use user history to obtain the notional net deposits instead of totalDeposits - totalWithdraws since those are in base value
		const userNotionalNetDeposits = getVaultDepositorNotionalNetDeposits(
			vaultDepositorHistory ?? []
		);

		const totalNotionalEarnings = currentUserNotionalBalance.sub(
			userNotionalNetDeposits
		);

		return totalNotionalEarnings;
	}

	const stats: VaultStatProps[] = [
		{
			label: 'Your Balance',
			value: uiVaultConfig.isNotionalGrowthStrategy
				? currentUserNotionalBalance.toNum().toFixed(2)
				: currentUserBaseBalance.prettyPrint(true),
			marketSymbol: uiVaultConfig.isNotionalGrowthStrategy
				? 'USDC'
				: props.depositAssetConfig.symbol,
			loading:
				!isVaultDepositorLoaded ||
				(uiVaultConfig.isNotionalGrowthStrategy &&
					isVaultDepositorHistoryLoading),
			tooltip:
				'The amount of deposit asset that you currently have in the vault, after fees.',
			subValue: uiVaultConfig.isNotionalGrowthStrategy ? (
				<span className="flex items-center gap-1 typo-t4 text-text-secondary sm:typo-h5">
					<MarketIcon
						marketSymbol={props.depositAssetConfig.symbol}
						className="h-4 w-4"
					/>
					<span>{currentUserBaseBalance.prettyPrint()}</span>
				</span>
			) : undefined,
		},
		{
			label: 'Your Earnings (All Time)',
			value: uiVaultConfig.isNotionalGrowthStrategy
				? totalNotionalEarnings.toNum().toFixed(2)
				: totalBaseEarnings.prettyPrint(true),
			marketSymbol: uiVaultConfig.isNotionalGrowthStrategy
				? 'USDC'
				: props.depositAssetConfig.symbol,
			loading: !isVaultDepositorLoaded,
			tooltip: 'Your total earnings from the vault, after fees.',
			subValue: uiVaultConfig.isNotionalGrowthStrategy ? (
				<span className="flex items-center gap-1 typo-t4 text-text-secondary sm:typo-h5">
					<MarketIcon
						marketSymbol={props.depositAssetConfig.symbol}
						className="h-4 w-4"
					/>
					<span>{totalBaseEarnings.prettyPrint()}</span>
				</span>
			) : undefined,
		},
		{
			label: 'Your APY | ROI',
			value: (
				<div className="flex items-center gap-xs sm:items-end">
					<span
						className={cj(
							'typo-t4 sm:typo-h5',
							timeWeightedApy > 0
								? 'text-text-positive-green-button'
								: 'text-text-default'
						)}
					>
						{timeWeightedApy.toFixed(2)}%
					</span>
					<span className="sm:typo-h6 typo-t4 text-text-secondary">
						{timeWeightedRoi.toFixed(2)}%
					</span>
				</div>
			),
			loading: !isVaultDepositorLoaded,
			tooltip:
				'Time-weighted APY and ROI, net of all vault fees, based on your historical deposits and withdrawals',
		},
	];

	return <VaultStatsSkeleton stats={stats} />;
};
